import {
  Button,
  FieldGroupIconButton,
  Flex,
  Grid,
  Loader,
  SearchField,
  SelectField,
  TextField,
} from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";

import { useContext, useEffect, useState, useRef } from "react";
import toast from "react-hot-toast";
import LocalizedStrings from "react-localization";

import moment from "moment";
import { CModal, CModalBody, CModalHeader, CModalTitle } from "@coreui/react";
import { Close, EditCalendar } from "@mui/icons-material";

import {
  serviceOrdersByBranchID,
  serviceOrdersByCompanyID,
  serviceOrderLogsByCompanyID,
  exportedServiceOrdersByCompanyID,
} from "graphql/queries";
import CompanyBranchFilter from "components/companies/CompanyBranchFilter";
import { LayoutContext } from "layout/context/LayoutContext";

import SelectUser from "components/users/SelectUser";
import { serviceOrderStatus } from "config/constants";
import { globalStrings } from "config/translations/es";
import MultipleSelect from "components/utils/MultipleSelect";
import DateRangeForm from "components/utils/DateRangeForm";
import { exportServiceOrderList } from "graphql/mutations";
import { convertToSlug } from "components/utils/functions";

const ExportServiceOrders = () => {
  const { me } = useContext(LayoutContext);
  const [exportLoading, setExportLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [generatedReport, setGeneratedReport] = useState(null);
  const [keyword, setKeyword] = useState("");
  const [userSelected, setUserSelected] = useState("");
  const [modalDateVisible, setModalDateVisible] = useState(false);
  const [searchBy, setSearchBy] = useState({
    company: {
      id: "",
      display: "",
    },
    branch: {
      id: "",
      display: "",
    },
    status: [],
    from: null,
    to: null,
  });

  const onChangeStatus = (value) => {
    setSearchBy((prev) => ({ ...prev, status: value }));
  };

  const onKeywordChange = (val) => {
    setKeyword(val);
  };

  const onSelectUser = (user) => {
    setUserSelected(user);
  };

  const onSubmitDateRange = (date, options) => {
    const { from, to } = date;
    setSearchBy((prev) => ({ ...prev, ...options, from, to }));
    setModalDateVisible(false);
  };

  const onClearDateRange = () => {
    setSearchBy((prev) => ({
      ...prev,
      from: null,
      to: null,
      dateRangeStatus: null,
    }));
  };

  const onDownload = () => {
    if (!generatedReport?.url) return;
    const link = document.createElement("a");
    link.href = generatedReport.url;
    link.download = "service_orders_export.zip";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportServiceOrdersFunc = async (ids) => {
    try {
      setExportLoading(true);

      const res = await API.graphql(
        graphqlOperation(exportServiceOrderList, {
          input: { ids, companyID: searchBy.company.id || me.companyID },
        })
      );

      if (!res.data.exportServiceOrderList?.success) {
        toast.error(
          res.data.exportServiceOrderList?.error ||
            strings.responses.unexpectedError
        );
        setExportLoading(false);
      }
    } catch (err) {
      console.log("Error export service order list:", err);
      toast.error(strings.responses.unexpectedError);
    }
  };

  async function fetchGeneratedReport(companyID) {
    try {
      setLoading(true);

      const exportedServiceOrders = await API.graphql(
        graphqlOperation(exportedServiceOrdersByCompanyID, {
          companyID,
          sortDirection: "DESC",
        })
      );
      let itemReport =
        exportedServiceOrders.data.exportedServiceOrdersByCompanyID.items[0];

      if (itemReport) {
        setGeneratedReport(itemReport);
        setExportLoading(Boolean(itemReport.loading));
      } else {
        setGeneratedReport(null);
        setExportLoading(false);
      }
    } catch (err) {
      toast.error(strings.responses.errorFetch);
    } finally {
      setLoading(false);
    }
  }

  async function fetchServiceOrders() {
    let userExecutor = userSelected;
    let objOperation = {};
    let nameOperation = null;
    let nameOperationStr = "";
    try {
      setLoading(true);

      if (!searchBy.company.id && !me.companyID) return;

      if (!!searchBy.branch.id) {
        objOperation = {
          branchID: searchBy.branch.id,
          sortDirection: "DESC",
        };
        nameOperation = serviceOrdersByBranchID;
        nameOperationStr = "serviceOrdersByBranchID";
      } else if (!!searchBy.company.id || !!me.companyID) {
        objOperation = {
          companyID: searchBy.company.id || me.companyID,
          sortDirection: "DESC",
        };
        nameOperation = serviceOrdersByCompanyID;
        nameOperationStr = "serviceOrdersByCompanyID";
      }

      if (me.isTechnician) userExecutor = me.id;

      let accumulated = [];
      let nextTokenTemp = null;

      do {
        const objFilter = {
          ...(userExecutor ? { userExecutor: { eq: userExecutor } } : {}),
          ...(searchBy.status?.length > 0
            ? {
                or: searchBy.status.map((st) => ({
                  status: { eq: st },
                })),
              }
            : {}),
          deletedAt: { attributeExists: false },
        };

        if (searchBy.from && searchBy.to) {
          const serviceOrderLogs = await API.graphql(
            graphqlOperation(serviceOrderLogsByCompanyID, {
              companyID: searchBy.company.id || me.companyID,
              filter: {
                createdAt: {
                  between: [
                    searchBy.from.toISOString(),
                    searchBy.to.toISOString(),
                  ],
                },
                status: { eq: searchBy.dateRangeStatus },
              },
            })
          );
          let serviceOrderLogsItems =
            serviceOrderLogs.data.serviceOrderLogsByCompanyID.items;

          const idsExecutedInDateRange = [
            ...new Set(serviceOrderLogsItems.map((log) => log.serviceOrderID)),
          ];

          if (idsExecutedInDateRange.length > 0) {
            objFilter.or = [
              ...(objFilter.or || []),
              ...idsExecutedInDateRange.map((id) => ({
                id: { eq: id },
              })),
            ];
          } else {
            setLoading(false);
            toast.error(strings.responses.serviceOrdersNotFound);
            return;
          }
        }

        const serviceOrdersData = await API.graphql(
          graphqlOperation(nameOperation, {
            ...objOperation,
            nextToken: nextTokenTemp,
            filter: keyword
              ? {
                  and: [
                    {
                      or: [
                        {
                          code: { contains: keyword },
                        },
                        {
                          description: { contains: keyword },
                        },
                        {
                          title: { contains: keyword },
                        },
                        { slug: { contains: convertToSlug(keyword) } },
                      ],
                    },
                  ],
                  ...objFilter,
                }
              : objFilter,
          })
        );
        const pageItems =
          serviceOrdersData?.data?.[nameOperationStr]?.items ?? [];
        const responseNextToken =
          serviceOrdersData?.data?.[nameOperationStr]?.nextToken ?? null;

        accumulated = [...accumulated, ...pageItems];

        nextTokenTemp = responseNextToken;
      } while (nextTokenTemp);

      if (accumulated.length <= 0) {
        toast.error(strings.responses.serviceOrdersNotFound);
        return;
      }

      exportServiceOrdersFunc(accumulated.map((so) => so.id));
    } catch (err) {
      console.log("Error fetching service orders:", err);
      toast.error(strings.responses.errorFetch);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (searchBy.company.id || me.companyID)
      fetchGeneratedReport(searchBy.company.id || me.companyID);
    else {
      setGeneratedReport(null);
      setExportLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchBy.company.id]);

  const pollIntervalRef = useRef(null);
  useEffect(() => {
    const companyID = searchBy.company.id || me.companyID;
    if (!companyID) return;

    if (exportLoading) {
      pollIntervalRef.current = setInterval(() => {
        fetchGeneratedReport(companyID);
      }, 5000);
    } else {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [exportLoading, searchBy.company.id, me.companyID]);

  return (
    <div>
      <Flex justifyContent="space-between">
        <span className="fs-3">{strings.title}</span>
      </Flex>
      <div className="p-3">
        <Grid
          className="mb-3"
          templateColumns={{ medium: "1fr", xl: "1fr 1fr 1fr" }}
          rowGap="15px"
          columnGap="15px"
        >
          <CompanyBranchFilter searchBy={searchBy} setSearchBy={setSearchBy} />
          <SearchField
            label={strings.table.search.label}
            placeholder={strings.table.search.placeholder}
            value={keyword}
            onChange={(e) => onKeywordChange(e.target.value)}
            onClear={() => onKeywordChange("")}
          />
          {!me.isTechnician && (
            <SelectUser
              placeholder={strings.headers.searchTechnician}
              onSelect={onSelectUser}
            />
          )}
          <MultipleSelect
            options={Object.entries(globalStrings.serviceOrderStatus).map(
              (opt) => ({ label: opt[1], value: opt[0] })
            )}
            values={searchBy.status}
            onChange={onChangeStatus}
            placeholder={strings.table.search.byState}
          />
          <div style={{ marginTop: "-8px" }}>
            <TextField
              readOnly
              value={
                searchBy.from
                  ? `${moment(searchBy.from).format(
                      "YYYY-MM-DD HH:mm"
                    )} - ${moment(searchBy.to).format("YYYY-MM-DD HH:mm")} (${
                      globalStrings.serviceOrderStatus[searchBy.dateRangeStatus]
                    })`
                  : ""
              }
              onClick={() => setModalDateVisible(true)}
              placeholder={strings.searchByDateRange}
              innerEndComponent={
                searchBy.from && (
                  <FieldGroupIconButton
                    variation="link"
                    onClick={onClearDateRange}
                  >
                    <Close fontSize="16px" />
                  </FieldGroupIconButton>
                )
              }
              outerEndComponent={
                <FieldGroupIconButton onClick={() => setModalDateVisible(true)}>
                  <EditCalendar fontSize="small" />
                </FieldGroupIconButton>
              }
            />
          </div>
        </Grid>
        <Flex gap="10px">
          <Button
            isDisabled={
              loading ||
              exportLoading ||
              (!searchBy.company.id && !me.companyID)
            }
            style={styles.button}
            children={strings.generate}
            variation="primary"
            onClick={fetchServiceOrders}
          />

          {(loading || exportLoading) && (
            <Flex alignItems="center" gap="5px">
              <Loader />
              <span className="text-muted">
                {exportLoading ? strings.exporting : strings.loading}
              </span>
            </Flex>
          )}

          {generatedReport?.url && !exportLoading && !loading && (
            <>
              <Button
                style={styles.button}
                children={strings.download}
                variation="link"
                onClick={onDownload}
              />
              <Flex alignItems="center" gap="5px">
                <span className="text-muted">
                  {`${strings.reportGenerated} ${moment(
                    generatedReport?.createdAt
                  ).format("YYYY-MM-DD HH:mm:ss")}`}
                </span>
              </Flex>
            </>
          )}
        </Flex>
      </div>
      <CModal
        alignment="center"
        visible={modalDateVisible}
        onClose={() => setModalDateVisible(false)}
        portal={false}
        backdrop="static"
      >
        <CModalHeader>
          <CModalTitle>{strings.searchByDateRange}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <DateRangeForm
            initialValues={{
              from: searchBy.from && moment(searchBy.from).toDate(),
              to: searchBy.to && moment(searchBy.to).toDate(),
              options: {
                dateRangeStatus:
                  searchBy.dateRangeStatus || serviceOrderStatus.created,
              },
            }}
            onSubmit={onSubmitDateRange}
            withHours
            content={({ options, setOptions }) => (
              <>
                <div style={{ display: "flex", gap: 10 }}>
                  <SelectField
                    label={strings.status}
                    value={options?.dateRangeStatus || ""}
                    onChange={(e) =>
                      setOptions((o) => ({
                        ...o,
                        dateRangeStatus: e.target.value,
                      }))
                    }
                  >
                    {Object.entries(globalStrings.serviceOrderStatus).map(
                      (v) => (
                        <option key={v[0]} value={v[0]}>
                          {v[1] || "--"}
                        </option>
                      )
                    )}
                  </SelectField>
                </div>
              </>
            )}
          />
        </CModalBody>
      </CModal>
    </div>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Exportar Órdenes de Servicio",
    status: "Estado",
    searchByDateRange: "Buscar por rango de fecha y estado",
    headers: {
      searchTechnician: "Buscar técnico",
    },
    responses: {
      errorFetch: "Error al consultar orden de servicio",
      unexpectedError: "Ha ocurrido un error inesperado",
      serviceOrdersNotFound: "No se encontraron órdenes de servicio",
    },
    table: {
      search: {
        label: "Buscar",
        placeholder: "Buscar por palabra clave...",
        byState: "Buscar por estado",
      },
    },
    generate: "Generar Reporte",
    download: "Descargar",
    exporting: "Exportando órdenes...",
    loading: "Cargando...",
    reportGenerated: "Reporte generado el",
  },
});

const styles = {
  buttonAdd: { float: "right" },
  modal: { width: "250px" },
  collapseCard: { marginTop: "15px" },
  buttonPrevious: { marginLeft: "15px" },
  buttonNext: { float: "right", marginRight: "15px" },
};

export default ExportServiceOrders;
